/**
 * Large Data Storage Service using IndexedDB
 * Handles storage of large Excel datasets that exceed sessionStorage limits
 */

import { openDB } from 'idb';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { API_LIMITS } from '@/constants/apiLimits';

const DB_NAME = 'ExcelUploadsDB';
const DB_VERSION = 2; // Incremented to trigger upgrade for chunks store
const STORE_NAME = 'uploads';
const CHUNK_SIZE = API_LIMITS.MAX_EXCEL_ROWS; // Process in chunks matching API limit

class LargeDataStorage {
  constructor() {
    this.db = null;
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    if (this.db) return this.db;

    try {
      this.db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion) {
          debugLog(`Upgrading IndexedDB from version ${oldVersion} to ${newVersion}`);

          // Create main uploads store
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            debugLog('Creating uploads store...');
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'uploadId' });
            store.createIndex('timestamp', 'timestamp');
          }

          // Create chunks store for large datasets
          const chunksStoreName = `${STORE_NAME}_chunks`;
          if (!db.objectStoreNames.contains(chunksStoreName)) {
            debugLog('Creating chunks store...');
            const chunksStore = db.createObjectStore(chunksStoreName, { keyPath: ['uploadId', 'chunkIndex'] });
            chunksStore.createIndex('uploadId', 'uploadId');
          }
        },
        blocked() {
          debugWarn('IndexedDB upgrade blocked. Please close other tabs.');
        },
        blocking() {
          debugWarn('IndexedDB blocking other connections.');
        },
      });

      debugLog('IndexedDB initialized successfully');
      return this.db;
    } catch (error) {
      debugError('Failed to initialize IndexedDB:', error);

      // If initialization fails, try to delete and recreate the database
      try {
        debugLog('Attempting to reset database...');
        await this.resetDatabase();
        return await this.init();
      } catch (resetError) {
        debugError('Failed to reset database:', resetError);
        throw new Error('Storage initialization failed');
      }
    }
  }

  /**
   * Reset the database (delete and recreate)
   */
  async resetDatabase() {
    try {
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      
      // Delete the database
      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
      await new Promise((resolve, reject) => {
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
        deleteRequest.onblocked = () => {
          debugWarn('Database deletion blocked. Please close other tabs.');
          // Continue anyway after a timeout
          setTimeout(resolve, 1000);
        };
      });

      debugLog('Database reset successfully');
    } catch (error) {
      debugError('Failed to reset database:', error);
      throw error;
    }
  }

  /**
   * Store large Excel data in chunks
   * @param {string} uploadId - Unique upload identifier
   * @param {Object} data - Excel data to store
   * @returns {Promise<void>}
   */
  async storeExcelData(uploadId, data) {
    await this.init();

    try {
      const { headers, dataRows, ...metadata } = data;
      
      // Create chunked data structure
      const chunks = [];
      const chunkSize = CHUNK_SIZE;
      
      for (let i = 0; i < dataRows.length; i += chunkSize) {
        const chunk = dataRows.slice(i, i + chunkSize);
        chunks.push({
          chunkIndex: Math.floor(i / chunkSize),
          startRow: i,
          endRow: Math.min(i + chunkSize - 1, dataRows.length - 1),
          data: chunk,
          rowCount: chunk.length
        });
      }

      // Store the upload record
      const uploadRecord = {
        uploadId,
        timestamp: Date.now(),
        fileName: metadata.fileName,
        headers,
        totalRows: dataRows.length,
        totalChunks: chunks.length,
        chunkSize,
        metadata,
        // Store first chunk inline for quick access
        firstChunk: chunks[0]?.data || [],
        sampleData: this.createSampleData(headers, dataRows.slice(0, 10))
      };

      // Store main record
      await this.db.put(STORE_NAME, uploadRecord);

      // Store chunks separately for large datasets
      if (chunks.length > 1) {
        const chunksStoreName = `${STORE_NAME}_chunks`;

        // Ensure chunks store exists
        if (!this.db.objectStoreNames.contains(chunksStoreName)) {
          debugError('Chunks store not found, recreating database...');
          this.db.close();
          this.db = null;
          await this.init(); // Reinitialize with proper stores
        }
        
        const tx = this.db.transaction(chunksStoreName, 'readwrite');
        const store = tx.objectStore(chunksStoreName);
        
        for (const chunk of chunks) {
          await store.put({
            uploadId,
            chunkIndex: chunk.chunkIndex,
            startRow: chunk.startRow,
            endRow: chunk.endRow,
            data: chunk.data,
            rowCount: chunk.rowCount
          });
        }
        
        await tx.complete;
      }

      debugLog(`Stored ${dataRows.length} rows in ${chunks.length} chunks for upload ${uploadId}`);
    } catch (error) {
      debugError('Failed to store Excel data:', error);
      throw new Error(`Storage failed: ${error.message}`);
    }
  }

  /**
   * Retrieve Excel data by upload ID
   * @param {string} uploadId - Upload identifier
   * @returns {Promise<Object>} Excel data
   */
  async getExcelData(uploadId) {
    await this.init();

    try {
      const uploadRecord = await this.db.get(STORE_NAME, uploadId);
      if (!uploadRecord) {
        throw new Error('Upload session not found');
      }

      // For small datasets, return data directly
      if (uploadRecord.totalChunks <= 1) {
        return {
          uploadId: uploadRecord.uploadId,
          fileName: uploadRecord.fileName,
          headers: uploadRecord.headers,
          dataRows: uploadRecord.firstChunk,
          totalRows: uploadRecord.totalRows,
          ...uploadRecord.metadata
        };
      }

      // For large datasets, we'll return metadata and provide chunk loading methods
      return {
        uploadId: uploadRecord.uploadId,
        fileName: uploadRecord.fileName,
        headers: uploadRecord.headers,
        totalRows: uploadRecord.totalRows,
        totalChunks: uploadRecord.totalChunks,
        chunkSize: uploadRecord.chunkSize,
        sampleData: uploadRecord.sampleData,
        ...uploadRecord.metadata,
        // Method to load specific chunk
        loadChunk: async (chunkIndex) => this.loadChunk(uploadId, chunkIndex),
        // Method to load all data (use with caution for large datasets)
        loadAllData: async () => this.loadAllChunks(uploadId)
      };
    } catch (error) {
      debugError('Failed to retrieve Excel data:', error);
      throw new Error(`Data retrieval failed: ${error.message}`);
    }
  }

  /**
   * Load a specific chunk of data
   * @param {string} uploadId - Upload identifier
   * @param {number} chunkIndex - Chunk index to load
   * @returns {Promise<Array>} Chunk data
   */
  async loadChunk(uploadId, chunkIndex) {
    await this.init();

    try {
      const chunksStoreName = `${STORE_NAME}_chunks`;


      // Check if chunks store exists
      if (!this.db.objectStoreNames.contains(chunksStoreName)) {
        debugWarn(`Chunks store not found for uploadId: ${uploadId}`);
        return [];
      }

      const chunkRecord = await this.db.get(chunksStoreName, [uploadId, chunkIndex]);
      return chunkRecord ? chunkRecord.data : [];
    } catch (error) {
      debugError(`Failed to load chunk ${chunkIndex}:`, error);
      return [];
    }
  }

  /**
   * Load all chunks (use carefully with large datasets)
   * @param {string} uploadId - Upload identifier
   * @returns {Promise<Array>} All data rows
   */
  async loadAllChunks(uploadId) {
    await this.init();

    try {
      const uploadRecord = await this.db.get(STORE_NAME, uploadId);
      if (!uploadRecord) {
        throw new Error('Upload session not found');
      }

      if (uploadRecord.totalChunks <= 1) {
        return uploadRecord.firstChunk;
      }

      const allRows = [];
      for (let i = 0; i < uploadRecord.totalChunks; i++) {
        const chunk = await this.loadChunk(uploadId, i);
        allRows.push(...chunk);
      }

      return allRows;
    } catch (error) {
      debugError('Failed to load all chunks:', error);
      throw new Error(`Failed to load complete dataset: ${error.message}`);
    }
  }

  /**
   * Process data in chunks to avoid memory issues
   * @param {string} uploadId - Upload identifier
   * @param {Function} processor - Function to process each chunk
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Array>} Processed results
   */
  async processDataInChunks(uploadId, processor, onProgress) {
    await this.init();

    try {
      const uploadRecord = await this.db.get(STORE_NAME, uploadId);
      if (!uploadRecord) {
        throw new Error('Upload session not found');
      }

      const results = [];
      const totalChunks = uploadRecord.totalChunks;

      for (let i = 0; i < totalChunks; i++) {
        // Load chunk
        const chunkData = await this.loadChunk(uploadId, i);
        
        // Process chunk
        const chunkResults = await processor(chunkData, i, {
          chunkIndex: i,
          totalChunks,
          startRow: i * uploadRecord.chunkSize,
          headers: uploadRecord.headers
        });

        if (chunkResults && chunkResults.length > 0) {
          results.push(...chunkResults);
        }

        // Report progress
        if (onProgress) {
          onProgress({
            completed: i + 1,
            total: totalChunks,
            percentage: Math.round(((i + 1) / totalChunks) * 100),
            processedRows: results.length
          });
        }

        // Yield control to prevent UI freezing
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      return results;
    } catch (error) {
      debugError('Failed to process data in chunks:', error);
      throw new Error(`Chunked processing failed: ${error.message}`);
    }
  }

  /**
   * Create sample data for quick preview
   * @param {Array} headers - Column headers
   * @param {Array} sampleRows - Sample data rows
   * @returns {Object} Sample data object
   */
  createSampleData(headers, sampleRows) {
    const sampleData = {};
    
    headers.forEach((header, index) => {
      if (header && header.trim()) {
        sampleData[header] = [];
        for (let i = 0; i < Math.min(5, sampleRows.length); i++) {
          if (sampleRows[i] && sampleRows[i][index] !== undefined) {
            const value = sampleRows[i][index];
            if (value !== null && value !== '') {
              sampleData[header].push(String(value).trim());
            }
          }
        }
      }
    });

    return sampleData;
  }

  /**
   * Clean up old upload data
   * @param {string} uploadId - Upload identifier to remove
   * @returns {Promise<void>}
   */
  async removeUpload(uploadId) {
    await this.init();

    try {
      // Remove main record
      await this.db.delete(STORE_NAME, uploadId);

      // Remove chunk records if chunks store exists
      const chunksStoreName = `${STORE_NAME}_chunks`;
      if (this.db.objectStoreNames.contains(chunksStoreName)) {
        const tx = this.db.transaction(chunksStoreName, 'readwrite');
        const store = tx.objectStore(chunksStoreName);
        
        // Delete all chunks for this upload
        const cursor = await store.openCursor();
        while (cursor) {
          if (cursor.value.uploadId === uploadId) {
            await cursor.delete();
          }
          await cursor.continue();
        }
        
        await tx.complete;
      }

      debugLog(`Cleaned up upload data for ${uploadId}`);
    } catch (error) {
      debugError('Failed to clean up upload data:', error);
    }
  }

  /**
   * Clean up old uploads (older than 24 hours)
   * @returns {Promise<void>}
   */
  async cleanupOldUploads() {
    await this.init();

    try {
      const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // 24 hours ago
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('timestamp');

      const cursor = await index.openCursor(IDBKeyRange.upperBound(cutoffTime));
      while (cursor) {
        await this.removeUpload(cursor.value.uploadId);
        await cursor.continue();
      }

      debugLog('Cleaned up old upload data');
    } catch (error) {
      debugError('Failed to cleanup old uploads:', error);
    }
  }

  /**
   * Get storage usage statistics
   * @returns {Promise<Object>} Storage statistics
   */
  async getStorageStats() {
    await this.init();

    try {
      const uploads = await this.db.getAll(STORE_NAME);
      const totalUploads = uploads.length;
      const totalRows = uploads.reduce((sum, upload) => sum + upload.totalRows, 0);

      return {
        totalUploads,
        totalRows,
        uploads: uploads.map(upload => ({
          uploadId: upload.uploadId,
          fileName: upload.fileName,
          totalRows: upload.totalRows,
          totalChunks: upload.totalChunks,
          timestamp: upload.timestamp,
          age: Date.now() - upload.timestamp
        }))
      };
    } catch (error) {
      debugError('Failed to get storage stats:', error);
      return { totalUploads: 0, totalRows: 0, uploads: [] };
    }
  }
}

// Export singleton instance
export default new LargeDataStorage();
